import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TodayIcon from '@mui/icons-material/Today';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CircularProgress from '@mui/material/CircularProgress';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SendIcon from '@mui/icons-material/Send';

import DatePickerField from '@/components/workflow/taskmanager/DatePickerField';
import AssigneePicker from '@/components/workflow/taskmanager/AssigneePicker';
import ChemistPicker from '@/components/workflow/taskmanager/ChemistPicker';
import {
  getTask, createTask, updateTask, completeTask, reopenTask, deleteTask,
  moveTask, uploadAttachment, deleteAttachment, downloadAttachment,
  getComments, addComment, deleteComment,
} from '@/services/taskService';
import { phappsRequest } from '@/services/apiService';
import { API_PATHS } from '@/config/apiConstants';
import { todayYMD } from '@/utils/taskDates';
import { IGNORE_PW_MANAGERS } from '@/utils/formHints';

const RECURRING_OPTIONS = [
  { value: 'none',        label: 'Does not repeat' },
  { value: 'daily',       label: 'Daily' },
  { value: 'weekly',      label: 'Weekly (same day)' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly',     label: 'Monthly' },
];

const fmtCreated = (s) => {
  if (!s) return '';
  const d = new Date(String(s).replace(' ', 'T'));
  return isNaN(d) ? '' : d.toLocaleString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

/**
 * Format a 'YYYY-MM-DD HH:MM:SS' comment timestamp for display.
 */
const fmtCommentTime = (s) => {
  if (!s) return '';
  const d = new Date(String(s).replace(' ', 'T'));
  return isNaN(d) ? '' : d.toLocaleString('en-AU', {
    day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit',
  });
};

const fmtSize = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const TaskDetailModal = ({ mode, taskId, defaultDate, itemType = 'task', staff, isAdmin, onClose, onSaved }) => {
  const navigate = useNavigate();
  const isCreate = mode === 'create';
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving]   = useState(false);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const fileRef = useRef(null);
  const titleRef = useRef(null);

  // 'task' | 'event' — fixed at create from prop; for edit it comes from the loaded item.
  const [type, setType] = useState(itemType);
  const isEvent = type === 'event';
  const showComments = !isEvent && !isCreate;

  const [form, setForm] = useState({
    title: '', notes: '', assignees: [], chemist: null,
    due_date: defaultDate || todayYMD(),
    end_date: defaultDate || todayYMD(),
    is_urgent: false, recurring: 'none',
  });
  const [status, setStatus]           = useState('open');
  const [attachments, setAttachments] = useState([]);
  const [comments, setComments]       = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentBusy, setCommentBusy] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [meta, setMeta]               = useState({ createdByName: null, createdAt: null });

  // Team Calendar conflict check
  const [conflicts, setConflicts]               = useState([]);
  const [conflictsPending, setConflictsPending] = useState(false);

  const set = (k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      // Keep an event's end on/after its start.
      if (k === 'due_date' && isEvent && next.end_date < v) next.end_date = v;
      return next;
    });
    setFieldErrors((fe) => (fe[k] ? { ...fe, [k]: undefined } : fe));
  };

  const validate = () => {
    const errs = {};
    if (!form.title.trim()) errs.title = `Please enter a${isEvent ? 'n event' : ' task'} title.`;
    if (!form.due_date)     errs.due_date = isEvent ? 'Please choose a start date.' : 'Please choose a due date.';
    if (isEvent && form.end_date && form.end_date < form.due_date) errs.end_date = 'End date must be on or after the start date.';
    return errs;
  };

  const validateField = (k) => {
    const errs = validate();
    setFieldErrors((fe) => ({ ...fe, [k]: errs[k] }));
  };

  const reload = useCallback(async () => {
    if (isCreate) return;
    setLoading(true);
    try {
      const t = await getTask(taskId);
      setType(t.item_type || 'task');
      setForm({
        title: t.title || '',
        notes: t.notes || '',
        assignees: (t.assignees || []).map((a) => a.id),
        chemist: t.chemist_id ? { id: t.chemist_id, name: t.chemist_name || `Chemist #${t.chemist_id}`, phuuid: t.chemist_uuid || null } : null,
        due_date: t.due_date,
        end_date: t.end_date || t.due_date,
        is_urgent: !!t.is_urgent,
        recurring: t.recurring || 'none',
      });
      setStatus(t.status);
      setAttachments(t.attachments || []);
      setMeta({ createdByName: t.created_by_name || null, createdAt: t.created_at || null });
      getComments(taskId).then(setComments).catch(() => setComments([]));
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [isCreate, taskId]);

  useEffect(() => { reload(); }, [reload]);

  // Debounced Team Calendar conflict check — runs when dates or assignees change
  const assigneesKey = useMemo(() => form.assignees.slice().sort().join(','), [form.assignees]);
  useEffect(() => {
    const startDate = form.due_date;
    const endDate   = isEvent ? (form.end_date || form.due_date) : form.due_date;
    const ids       = form.assignees.map(Number).filter(Boolean);

    if (!startDate || ids.length === 0) { setConflicts([]); return; }

    setConflictsPending(true);

    const timer = setTimeout(async () => {
      try {
        const res = await phappsRequest(
          API_PATHS.TEAM_CALENDAR_CONFLICTS, 'GET', null,
          { startDate, endDate, managerIds: ids.join(',') },
        );
        setConflicts(res?.conflicts || []);
      } catch {
        setConflicts([]); // silently ignore — don't block save on check failure
      } finally {
        setConflictsPending(false);
      }
    }, 450);

    return () => { clearTimeout(timer); setConflictsPending(false); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.due_date, form.end_date, assigneesKey, isEvent]);

  const overdue = !isEvent && status === 'open' && form.due_date < todayYMD();

  const buildBody = () => {
    const base = {
      title: form.title.trim(),
      notes: form.notes,
      assignees: form.assignees.map(Number),
      chemist_id: form.chemist?.id || null,
      due_date: form.due_date,
    };
    if (isEvent) return { ...(isCreate ? { item_type: 'event' } : {}), ...base, end_date: form.end_date };
    return { ...(isCreate ? { item_type: 'task' } : {}), ...base, is_urgent: form.is_urgent, recurring: form.recurring };
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      if (errs.title) titleRef.current?.focus();
      return;
    }
    setFieldErrors({});
    setError(null);

    setSaving(true);
    try {
      if (isCreate) await createTask(buildBody());
      else          await updateTask(taskId, buildBody());
      onSaved();
      onClose();
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  const handleComplete = async () => {
    setBusy(true);
    try {
      if (status === 'completed') { await reopenTask(taskId); setStatus('open'); }
      else { await completeTask(taskId); onSaved(); onClose(); return; }
      onSaved();
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const handleMoveToToday = async () => {
    setBusy(true);
    try { await moveTask(taskId, todayYMD()); set('due_date', todayYMD()); onSaved(); }
    catch (e) { setError(e.message); }
    setBusy(false);
  };


  const handleClose = () => {
    setFieldErrors({});
    onClose();
  };

  const handleDelete = async () => {
    setBusy(true);
    try { await deleteTask(taskId); onSaved(); onClose(); }
    catch (e) { setError(e.message); setConfirmDelete(false); setBusy(false); }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true); setError(null);
    try {
      let latest = attachments;
      for (const f of files) { const res = await uploadAttachment(taskId, f); latest = res.attachments || latest; }
      setAttachments(latest);
      onSaved();
    } catch (e) { setError(e.message); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submitComment = async () => {
    const text = commentText.trim();
    if (!text || commentBusy) return;
    setCommentBusy(true);
    try {
      await addComment(taskId, text);
      setCommentText('');
      setComments(await getComments(taskId));
    } catch (e) { setError(e.message); }
    setCommentBusy(false);
  };

  const removeComment = async (commentId) => {
    try {
      await deleteComment(commentId);
      setComments(await getComments(taskId));
    } catch (e) { setError(e.message); }
  };

  const handleDeleteAttachment = async (attId) => {
    setBusy(true);
    try { await deleteAttachment(attId); setAttachments((a) => a.filter((x) => x.id !== attId)); onSaved(); }
    catch (e) { setError(e.message); }
    setBusy(false);
  };

  const inputCls = 'mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 bg-white transition';
  const okRing   = 'border-gray-200 focus:ring-blue-300';
  const errRing  = 'border-red-400 focus:ring-red-300 bg-red-50/40';
  const fieldCls = (k) => `${inputCls} ${fieldErrors[k] ? errRing : okRing}`;
  const labelCls = 'text-xs font-semibold text-gray-500 uppercase tracking-wide';
  const FieldError = ({ k }) => fieldErrors[k]
    ? <p className="mt-1 flex items-center gap-1 text-xs text-red-600"><ErrorOutlineIcon style={{ fontSize: 13 }} />{fieldErrors[k]}</p>
    : null;

  const headerColor = overdue ? 'bg-red-500' : isEvent ? 'bg-indigo-600' : 'bg-[#002169]';
  const headerTitle = isCreate
    ? (isEvent ? 'New Event' : 'New Task')
    : (isEvent ? 'Event Details' : status === 'completed' ? 'Completed Task' : 'Task Details');
  const accentBtn = isEvent ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-[#FFAF3C] text-[#002169] hover:bg-amber-400';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full max-h-[92vh] flex flex-col ${showComments ? 'max-w-5xl' : 'max-w-lg'}`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 rounded-t-2xl flex-shrink-0 ${headerColor}`}>
          <h2 className="text-lg font-bold text-white truncate flex items-center gap-2">
            {isEvent && <EventOutlinedIcon style={{ fontSize: 19 }} />}{headerTitle}
          </h2>
          <button type="button" onMouseDown={e => e.preventDefault()} onClick={handleClose} className="text-white/70 hover:text-white transition"><CloseIcon /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center py-10"><CircularProgress size={26} style={{ color: '#002169' }} /></div>
          ) : (
            <div className={showComments ? 'grid gap-6 lg:grid-cols-[minmax(0,1fr)_24rem] items-start' : ''}>
              <div className="space-y-4 min-w-0">
              {overdue && !isCreate && (
                <div className="flex items-center justify-between gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <span className="text-sm text-red-700 font-medium">This task is overdue.</span>
                  <button type="button" onClick={handleMoveToToday} disabled={busy}
                    className="flex items-center gap-1 text-xs font-bold text-white bg-red-600 hover:bg-red-700 px-2.5 py-1.5 rounded-lg transition disabled:opacity-50">
                    <TodayIcon style={{ fontSize: 14 }} /> Move to today
                  </button>
                </div>
              )}

              {!isCreate && meta.createdByName && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5">
                  <PersonOutlineIcon style={{ fontSize: 14 }} className="text-gray-400" />
                  <span>
                    Created by <span className="font-semibold text-gray-700">{meta.createdByName}</span>
                    {fmtCreated(meta.createdAt) ? ` · ${fmtCreated(meta.createdAt)}` : ''}
                  </span>
                </div>
              )}

              <div>
                <label className={labelCls}>Title *</label>
                <input ref={titleRef} autoFocus={isCreate} value={form.title}
                  onChange={(e) => set('title', e.target.value)}
                  onBlur={() => validateField('title')}
                  aria-invalid={!!fieldErrors.title}
                  {...IGNORE_PW_MANAGERS}
                  placeholder={isEvent ? 'e.g. APP Conference' : 'What needs doing?'} className={fieldCls('title')} />
                <FieldError k="title" />
              </div>

              {/* Dates */}
              {isEvent ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Start date *</label>
                    <DatePickerField value={form.due_date} onChange={(ymd) => set('due_date', ymd)}
                      onClose={() => validateField('due_date')} invalid={!!fieldErrors.due_date} />
                    <FieldError k="due_date" />
                  </div>
                  <div>
                    <label className={labelCls}>End date *</label>
                    <DatePickerField value={form.end_date} onChange={(ymd) => set('end_date', ymd)}
                      onClose={() => validateField('end_date')} invalid={!!fieldErrors.end_date} />
                    <FieldError k="end_date" />
                  </div>
                </div>
              ) : (
                <div>
                  <label className={labelCls}>Due date *</label>
                  <DatePickerField value={form.due_date} onChange={(ymd) => set('due_date', ymd)}
                    onClose={() => validateField('due_date')} invalid={!!fieldErrors.due_date} />
                  <FieldError k="due_date" />
                </div>
              )}

              {/* People */}
              <div>
                <label className={labelCls}>{isEvent ? 'Attendees' : 'Assigned to'}</label>
                <AssigneePicker staff={staff} value={form.assignees} onChange={(ids) => set('assignees', ids)}
                  placeholder={isEvent ? 'Add attendees…' : 'Assign people…'} />

              </div>

              {/* Linked pharmacy (optional) */}
              <div>
                <div className="flex items-center justify-between">
                  <label className={labelCls}>Linked pharmacy</label>
                  {!isCreate && form.chemist?.phuuid && (
                    <button
                      type="button"
                      onClick={() => { onClose(); navigate(`/admin-functions/chemist/${form.chemist.phuuid}`); }}
                      className="flex items-center gap-1 text-xs font-semibold text-sky-600 hover:text-sky-800 transition"
                    >
                      <OpenInNewIcon style={{ fontSize: 13 }} />
                      View pharmacy
                    </button>
                  )}
                </div>
                <ChemistPicker value={form.chemist} onChange={(c) => set('chemist', c)} />
              </div>

              {/* Task-only: recurring + urgency */}
              {!isEvent && (
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div>
                    <label className={labelCls}>Repeats</label>
                    <select value={form.recurring} onChange={(e) => set('recurring', e.target.value)} className={`${inputCls} ${okRing}`}>
                      {RECURRING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <button type="button" onClick={() => set('is_urgent', !form.is_urgent)}
                    className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border transition
                      ${form.is_urgent ? 'bg-amber-50 border-amber-300 text-amber-700' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                    {form.is_urgent ? <StarIcon style={{ fontSize: 17 }} /> : <StarBorderIcon style={{ fontSize: 17 }} />}
                    {form.is_urgent ? 'Urgent' : 'Mark urgent'}
                  </button>
                </div>
              )}

              <div>
                <label className={labelCls}>Notes</label>
                <textarea rows={isEvent ? 3 : 4} value={form.notes} onChange={(e) => set('notes', e.target.value)}
                  placeholder="Optional notes…" className={`${inputCls} ${okRing} resize-none`} />
              </div>

              {/* Attachments — tasks only, existing items only */}
              {!isEvent && !isCreate && (
                <div>
                  <div className="flex items-center justify-between">
                    <label className={labelCls}>Attachments</label>
                    <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
                      className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800 transition disabled:opacity-50">
                      <AttachFileIcon style={{ fontSize: 14 }} /> Add file
                    </button>
                    <input ref={fileRef} type="file" multiple className="hidden" onChange={handleUpload} />
                  </div>
                  <div className="mt-1.5 space-y-1.5">
                    {attachments.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">No files attached.</p>
                    ) : attachments.map((a) => (
                      <div key={a.id} className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5">
                        <AttachFileIcon style={{ fontSize: 14 }} className="text-gray-400 flex-shrink-0" />
                        <button type="button" onClick={() => downloadAttachment(a.id, a.original_name)}
                          className="flex-1 min-w-0 text-left text-sm text-gray-700 hover:text-blue-700 truncate">
                          {a.original_name}
                        </button>
                        <span className="text-[10px] text-gray-400 flex-shrink-0">{fmtSize(a.size_bytes)}</span>
                        <button type="button" title="Download" onClick={() => downloadAttachment(a.id, a.original_name)}
                          className="text-gray-400 hover:text-blue-600 flex-shrink-0"><DownloadIcon style={{ fontSize: 15 }} /></button>
                        <button type="button" title="Remove" onClick={() => handleDeleteAttachment(a.id)}
                          className="text-gray-400 hover:text-red-600 flex-shrink-0"><DeleteOutlineIcon style={{ fontSize: 15 }} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {conflicts.length > 0 && (
                <div className="flex items-start gap-2.5 rounded-lg px-3 py-2.5 border bg-amber-50 border-amber-200">
                  <WarningAmberIcon style={{ fontSize: 16 }} className="flex-shrink-0 mt-0.5 text-amber-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-amber-800">
                      {`Assignee${conflicts.length > 1 ? 's have' : ' has'} a Team Calendar event on this date`}
                    </p>
                    <ul className="mt-0.5 space-y-0.5">
                      {conflicts.map(c => (
                        <li key={c.id} className="text-[11px] text-amber-700">
                          <span className="font-semibold">{c.title}</span>
                          <span className="opacity-75">
                            {' · '}
                            {c.start_date === c.end_date
                              ? c.start_date
                              : `${c.start_date} – ${c.end_date}`}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-1 text-[11px] text-amber-700">
                      Create the task if there is no clash.
                    </p>
                  </div>
                </div>
              )}

              {error && <p className="text-sm text-red-500">{error}</p>}
              </div>

              {showComments && (
                <div className="min-w-0 lg:border-l lg:border-gray-100 lg:pl-6">
                <label className={labelCls}>
                  <span className="inline-flex items-center gap-1">
                    <ChatBubbleOutlineIcon style={{ fontSize: 14 }} />
                    Comments{comments.length > 0 ? ` (${comments.length})` : ''}
                  </span>
                </label>

                <div className="mt-1.5 flex gap-1.5">
                  <input
                    type="text"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitComment(); }
                    }}
                    disabled={commentBusy}
                    className="flex-1 min-w-0 rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm
                               focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400
                               disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={submitComment}
                    disabled={commentBusy || !commentText.trim()}
                    className="flex items-center gap-1 px-2.5 rounded-lg bg-blue-600 text-white text-xs font-semibold
                               hover:bg-blue-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {commentBusy
                      ? <CircularProgress size={13} sx={{ color: '#fff' }} />
                      : <SendIcon style={{ fontSize: 14 }} />}
                  </button>
                </div>

                <div className="mt-2 space-y-1.5 max-h-[60vh] overflow-y-auto pr-0.5">
                  {comments.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">No comments yet.</p>
                  ) : comments.map((cm) => (
                    <div
                      key={cm.id}
                      className={`group flex items-start gap-2 rounded-lg px-2.5 py-1.5 border ${
                        Number(cm.is_system)
                          ? 'bg-gray-50 border-gray-100'
                          : 'bg-white border-gray-200'
                      }`}
                    >
                      <span
                        className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: Number(cm.is_system) ? '#cbd5e1' : (cm.created_by_color || '#94a3b8') }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm break-words ${
                          Number(cm.is_system) ? 'text-gray-500 italic' : 'text-gray-800'
                        }`}>
                          {cm.comment}
                        </p>
                        <p
                          className="text-[10px] text-gray-400 mt-0.5"
                          title={cm.created_at ? `${cm.created_at} (server time)` : undefined}
                        >
                          {Number(cm.is_system) ? 'Automatic' : (cm.created_by_name || 'Unknown')}
                          {cm.created_at ? ` · ${fmtCommentTime(cm.created_at)}` : ''}
                        </p>
                      </div>
                      {!Number(cm.is_system) && (
                        <button
                          type="button"
                          title="Delete comment"
                          onClick={() => removeComment(cm.id)}
                          className="text-gray-300 hover:text-red-600 flex-shrink-0 opacity-0 group-hover:opacity-100 transition"
                        >
                          <DeleteOutlineIcon style={{ fontSize: 14 }} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && (
          <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0 flex items-center gap-2 flex-wrap">
            {!isCreate && isAdmin && (
              <button type="button" onClick={() => setConfirmDelete(true)} disabled={busy}
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold text-red-600 border border-red-200 hover:bg-red-50 transition disabled:opacity-50">
                <DeleteOutlineIcon style={{ fontSize: 16 }} /> Delete
              </button>
            )}
            <div className="flex-1" />
            <button type="button" onMouseDown={e => e.preventDefault()} onClick={handleClose}
              className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition">Cancel</button>
            {!isCreate && !isEvent && (
              <button type="button" onClick={handleComplete} disabled={busy}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold transition disabled:opacity-50
                  ${status === 'completed' ? 'border border-gray-200 text-gray-600 hover:bg-gray-50' : 'bg-green-600 text-white hover:bg-green-700'}`}>
                <CheckCircleOutlineIcon style={{ fontSize: 17 }} />
                {status === 'completed' ? 'Reopen' : 'Complete'}
              </button>
            )}
            <button type="button" onClick={handleSave}
              disabled={saving || conflictsPending}
              className={`px-5 py-2 rounded-lg text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed transition ${accentBtn}`}>
              {saving ? 'Saving…' : isCreate ? (isEvent ? 'Create Event' : 'Create Task') : 'Save'}
            </button>
          </div>
        )}

        {/* Delete confirmation — in-app card instead of a browser alert */}
        {confirmDelete && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 rounded-2xl p-4"
            onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) setConfirmDelete(false); }}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5">
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-9 h-9 rounded-full bg-red-50 flex items-center justify-center">
                  <DeleteOutlineIcon style={{ fontSize: 20 }} className="text-red-600" />
                </span>
                <div className="min-w-0">
                  <h3 className="text-sm font-bold text-gray-900">Delete this {isEvent ? 'event' : 'task'}?</h3>
                  <p className="mt-1 text-xs text-gray-500">
                    <span className="font-medium text-gray-700">{form.title.trim() || (isEvent ? 'This event' : 'This task')}</span> will be
                    permanently deleted. This cannot be undone.
                  </p>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-end gap-2">
                <button type="button" onClick={() => setConfirmDelete(false)} disabled={busy}
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition disabled:opacity-40">
                  Cancel
                </button>
                <button type="button" onClick={handleDelete} disabled={busy}
                  className="inline-flex items-center gap-1 px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition disabled:opacity-40">
                  <DeleteOutlineIcon style={{ fontSize: 16 }} />
                  {busy ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskDetailModal;
